import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { ContentType, Document, DocumentPopulated, Unpopulated } from '../../backend/types/document'
import { mutationHandler } from "../mutationHandler.ts"
import { MoneyFlowMongo } from '../../backend/types/moneyflow.ts'
import { getErrorCode } from "../handleResponse.ts"

export const useGetMoneyFlow = (documentId?: string, originalId?: string) => {
    const navigate = useNavigate()

    return useQuery<Document<MoneyFlowMongo>>({
        queryKey: [
            documentId ? `moneyflow/${documentId}` : `/public/get-moneyflow-from-shared-link/${originalId}`
        ],
        enabled: !!documentId || !!originalId,
        retry: (failureCount, error) => {
            const errorCode = getErrorCode(error)

            if (errorCode === '400' || errorCode === '403') {
                navigate({
                    pathname: "/",
                    search: createSearchParams({
                        errorId: 'bad-moneyflow-id'
                    }).toString()
                })
            }

            return failureCount < 3
        }
    })
}

const archiveDocument = ({ contentType, documentId }: {contentType: ContentType, documentId: string}) =>
    fetch(`/api/document/${contentType}/${documentId}/archive`, { method: 'PATCH' })

export const useArchiveDocument = (
    caseId: string
) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationKey: [caseId],
        mutationFn: mutationHandler(archiveDocument),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`case/${caseId}`] })
        },
    })
}

const updateDocument = async ({ documentId, changes, contentType }:
        {documentId: string, changes: Partial<Document<Unpopulated>>, contentType: ContentType }) =>
    fetch(`/api/document/${contentType}/${documentId}`,
        {
            method: 'PATCH',
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(changes)
        })


export const useUpdateDocument = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: mutationHandler<Document<Unpopulated>, typeof updateDocument>(updateDocument),
        onSuccess: (updated, params) => {
            const contentType = params.contentType.toLowerCase()

            queryClient.invalidateQueries({ queryKey: [`${contentType}/${params.documentId}`] })
            queryClient.setQueryData([`${contentType}/${params.documentId}`],
                (oldDocument: DocumentPopulated) => (oldDocument ? {
                    ...oldDocument,
                    name: updated.name,
                    lastModified: updated.lastModified
                } : updated))
        },
    })
}
